import React, { useCallback, useEffect, useState } from 'react'
import { Button, Card, Col, Form, Modal, Row, Spinner } from 'react-bootstrap'
import { Table, Tbody, Td, Th, Thead, Tr } from 'react-super-responsive-table'
import {
  BsFunnelFill,
  BsArrowClockwise,
  BsSearch,
  BsEyeFill,
  BsPencilFill,
} from 'react-icons/bs'
import { LuGraduationCap } from 'react-icons/lu'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'
import Cookies from 'js-cookie'
import toast from 'react-hot-toast'

import { BASE_URL } from './commonComponents/ApiEndpoints'
import SelectableField from './commonComponents/SelectableField'
import { useAuth } from './AuthContext'
import { useSidebar } from './SidebarContext'
import ProfileDropdown from './commonComponents/ProfileDropdown'
import { useInstitute } from './commonComponents/InstituteContext'

const toDatetimeLocalValue = (value) => {
  if (!value) return ''
  const raw = String(value).trim()
  // Prefer API-style "YYYY-MM-DD HH:mm:ss" / ISO without timezone shift
  const match = raw.match(/^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2})/)
  if (match) return `${match[1]}T${match[2]}`
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return ''
  const pad = (n) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

const toDateInputValue = (value) => {
  if (!value) return ''
  const raw = String(value).trim()
  const match = raw.match(/^(\d{4}-\d{2}-\d{2})/)
  if (match) return match[1]
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return ''
  const pad = (n) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

const formatDatetimeLocalToApi = (value) => {
  if (!value) return null
  const [datePart, timePart] = value.split('T')
  if (!datePart || !timePart) return null
  return `${datePart} ${timePart.length === 5 ? `${timePart}:00` : timePart}`
}

const formatDateToApi = (value) => {
  if (!value) return null
  return `${value} 00:00:00`
}

const emptyEditForm = {
  tc_issue_date: '',
  course_completion_date: '',
  reason: '',
  remarks: '',
}

const TransferCertificateList = () => {
  const { logout } = useAuth()
  const { isCollapsed } = useSidebar()
  const navigate = useNavigate()

  const [marginLeft, setMarginLeft] = useState('0px')
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= 768 : false,
  )
  const [logoError, setLogoError] = useState(false)
  const { logo } = useInstitute()

  const [resetKey, setResetKey] = useState(0)
  const [showFilterCard, setShowFilterCard] = useState(true)
  const [filters, setFilters] = useState({
    course: '',
    class: '',
    student: '',
  })

  const [studentsForSelection, setStudentsForSelection] = useState([])

  const [loading, setLoading] = useState(false)
  const [allTransferCertificates, setAllTransferCertificates] = useState([])
  const [transferCertificates, setTransferCertificates] = useState([])

  const [editingTc, setEditingTc] = useState(null)
  const [editFormData, setEditFormData] = useState(emptyEditForm)
  const [editFormErrors, setEditFormErrors] = useState({})
  const [loadingEditTc, setLoadingEditTc] = useState(false)
  const [updatingTc, setUpdatingTc] = useState(false)

  const instituteId = Cookies.get('institute_id')
  const token = (() => {
    const userCookie = Cookies.get('user')
    try {
      return userCookie ? JSON.parse(userCookie)?.token : null
    } catch {
      return null
    }
  })()

  const normalizeTransferCertificates = (data) => {
    if (Array.isArray(data)) return data
    if (data && typeof data === 'object') return [data]
    return []
  }

  const getAuthHeaders = useCallback(
    () => ({
      Authorization: token ? `Bearer ${token}` : undefined,
      'institute-Id': instituteId ? Number(instituteId) : undefined,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    }),
    [instituteId, token],
  )

  const fetchAllTransferCertificates = useCallback(async () => {
    if (!instituteId) return

    try {
      setLoading(true)
      const res = await axios.get(`${BASE_URL}/get/transfer-certificates/by-institute`, {
        headers: getAuthHeaders(),
      })

      const tcData = normalizeTransferCertificates(res?.data?.data)
      setAllTransferCertificates(tcData)
      setTransferCertificates(tcData)
    } catch (error) {
      const msg =
        error?.response?.data?.message ||
        error?.response?.data?.result ||
        'Failed to fetch Transfer Certificates'
      toast.error(msg)
      setAllTransferCertificates([])
      setTransferCertificates([])
    } finally {
      setLoading(false)
    }
  }, [getAuthHeaders, instituteId])

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 992) {
        const expandedWidth = 245
        const collapsedWidth = 85
        const gap = 1
        setMarginLeft(
          isCollapsed ? `${collapsedWidth + gap}px` : `${expandedWidth + gap}px`,
        )
      } else {
        setMarginLeft('0px')
      }
      setIsMobile(window.innerWidth <= 768)
    }
    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [isCollapsed])

  const handleSelect = (selectedValues) => {
    setFilters({
      course: selectedValues?.course ?? '',
      class: selectedValues?.class ?? '',
      student: selectedValues?.student ?? '',
    })
  }

  const handleRefresh = () => {
    setFilters({ course: '', class: '', student: '' })
    setTransferCertificates(allTransferCertificates)
    setResetKey((prev) => prev + 1)
  }

  const fillEditFormFromTc = (tc) => {
    setEditFormData({
      tc_issue_date: toDatetimeLocalValue(tc?.tc_issue_date),
      course_completion_date: toDateInputValue(tc?.course_completion_date),
      reason: tc?.reason || '',
      remarks: tc?.remarks || '',
    })
  }

  const openEditModal = async (tc) => {
    if (!tc?.id) {
      toast.error('Transfer Certificate id is missing.')
      return
    }

    setEditingTc(tc)
    fillEditFormFromTc(tc)
    setEditFormErrors({})
    setLoadingEditTc(true)

    try {
      const res = await axios.get(`${BASE_URL}/transfer-certificates/${tc.id}`, {
        headers: getAuthHeaders(),
      })
      const payload = res?.data?.data ?? res?.data
      if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
        setEditingTc({ ...tc, ...payload })
        fillEditFormFromTc({ ...tc, ...payload })
      }
    } catch (error) {
      // Keep list-row values if detail fetch fails; still allow editing.
      const msg =
        error?.response?.data?.message ||
        error?.response?.data?.result ||
        'Could not load latest TC details; showing list data.'
      toast.error(msg)
    } finally {
      setLoadingEditTc(false)
    }
  }

  const closeEditModal = () => {
    if (updatingTc || loadingEditTc) return
    setEditingTc(null)
    setEditFormData(emptyEditForm)
    setEditFormErrors({})
  }

  const handleEditInputChange = (e) => {
    const { name, value } = e.target
    setEditFormData((prev) => ({ ...prev, [name]: value }))
    if (editFormErrors[name]) {
      setEditFormErrors((prev) => ({ ...prev, [name]: '' }))
    }
  }

  const validateEditForm = () => {
    const errors = {}
    const reason = editFormData.reason?.trim() || ''

    if (reason.length > 255) {
      errors.reason = 'Reason cannot exceed 255 characters'
    }

    if (
      editFormData.course_completion_date &&
      !/^\d{4}-\d{2}-\d{2}$/.test(editFormData.course_completion_date)
    ) {
      errors.course_completion_date = 'Invalid completion date'
    }

    if (
      editFormData.tc_issue_date &&
      !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(editFormData.tc_issue_date)
    ) {
      errors.tc_issue_date = 'Invalid issue date'
    }

    setEditFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleEditSubmit = async (e) => {
    e.preventDefault()
    if (!editingTc?.id || !validateEditForm()) return

    if (!token) {
      toast.error('Authorization token missing. Please login again.')
      return
    }

    try {
      setUpdatingTc(true)
      setEditFormErrors({})

      const payload = {
        tc_issue_date:
          formatDatetimeLocalToApi(editFormData.tc_issue_date) ||
          editingTc.tc_issue_date,
        reason: editFormData.reason?.trim() || '',
        remarks: editFormData.remarks?.trim() || '',
      }

      if (editFormData.course_completion_date) {
        payload.course_completion_date = formatDateToApi(
          editFormData.course_completion_date,
        )
      } else {
        payload.course_completion_date = null
      }

      const response = await axios.put(
        `${BASE_URL}/transfer-certificates/${editingTc.id}`,
        payload,
        { headers: getAuthHeaders() },
      )

      const updated =
        response?.data?.data && typeof response.data.data === 'object'
          ? response.data.data
          : { ...editingTc, ...payload }

      const mergeUpdated = (list) =>
        list.map((item) =>
          Number(item?.id) === Number(editingTc.id) ? { ...item, ...updated } : item,
        )

      setAllTransferCertificates((prev) => mergeUpdated(prev))
      setTransferCertificates((prev) => mergeUpdated(prev))

      toast.success(
        response?.data?.message || 'Transfer Certificate updated successfully',
      )
      setEditingTc(null)
      setEditFormData(emptyEditForm)
      setEditFormErrors({})
    } catch (error) {
      const errorData = error.response?.data || {}

      if (errorData?.errors && typeof errorData.errors === 'object') {
        const mapped = {}
        Object.entries(errorData.errors).forEach(([field, msgs]) => {
          mapped[field] = Array.isArray(msgs) ? msgs.join(', ') : String(msgs)
        })
        setEditFormErrors(mapped)
      }

      toast.error(
        errorData?.message ||
          errorData?.result ||
          'Failed to update Transfer Certificate',
      )
    } finally {
      setUpdatingTc(false)
    }
  }

  useEffect(() => {
    fetchAllTransferCertificates()
  }, [fetchAllTransferCertificates])

  useEffect(() => {
    const courseId = filters?.course
    const studentId = filters?.student

    if (!instituteId || !courseId || !studentId) return

    const fetchByFilters = async () => {
      try {
        setLoading(true)

        const res = await axios.get(`${BASE_URL}/transfer-certificates`, {
          params: {
            institute_id: Number(instituteId),
            student_id: Number(studentId),
            course_id: Number(courseId),
          },
          headers: getAuthHeaders(),
        })

        const tcData = normalizeTransferCertificates(res?.data?.data)
        setTransferCertificates(tcData)
      } catch (error) {
        const msg =
          error?.response?.data?.message ||
          error?.response?.data?.result ||
          'Failed to fetch Transfer Certificates'
        toast.error(msg)
        setTransferCertificates([])
      } finally {
        setLoading(false)
      }
    }

    fetchByFilters()
  }, [filters.course, filters.student, getAuthHeaders, instituteId])

  return (
    <>
      <style>
        {`
          .transfer-page-title {
            letter-spacing: -0.02em;
            color: #223843;
            font-family: Inter, system-ui, sans-serif;
          }

          .tc-filter-dropdown-fix {
            overflow: visible !important;
            position: relative !important;
            z-index: 10 !important;
          }
          .tc-filter-dropdown-fix .rbt-menu {
            z-index: 9999 !important;
          }

          .tc-table-card {
            overflow: visible !important;
          }
          .tc-table-wrapper {
            overflow-x: auto;
            overflow-y: visible !important;
            max-height: none !important;
            position: relative;
          }
        `}
      </style>

      <div
        style={{
          marginLeft,
          transition: 'margin-left 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
          willChange: 'margin-left',
          WebkitTransition: 'margin-left 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
          position: 'relative',
          zIndex: 1,
          marginTop: '0px',
          paddingLeft: isCollapsed ? '12px' : '12px',
          paddingRight: isCollapsed ? '12px' : '12px',
        }}
      >
        <div
          className="container-fluid p-0 no-print"
          style={{
            marginTop: typeof window !== 'undefined' && window.innerWidth < 992 ? '55px' : '-12px',
            paddingLeft: '0px',
            paddingRight: '0px',
          }}
        >
          <div style={{ marginLeft: isMobile ? '-13px' : '-40px' }}>
            <div
              className="d-flex justify-content-between align-items-center"
              style={{
                background: '#F3F4F6',
                paddingLeft: '28px',
                paddingRight: '28px',
                paddingTop: '12px',
                paddingBottom: '12px',
                borderBottom: '1px solid #D1D3D4',
                width: '100%',
              }}
            >
              <div className="d-flex align-items-center gap-3">
                <div
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    overflow: 'hidden',
                    background: '#223843',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  {!logoError ? (
                    <img
                      src={logo}
                      alt="School Logo"
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      onError={() => setLogoError(true)}
                    />
                  ) : (
                    <LuGraduationCap size={20} style={{ color: '#ffffff' }} />
                  )}
                </div>
                <div style={{ minWidth: 0 }}>
                  <h3 className="m-0 fw-bold transfer-page-title text-start">Transfer Certificate</h3>
                  <p className="m-0 mt-0 text-start" style={{ fontSize: '13px', color: '#6b7280' }}>
                    View issued TC records using filters.
                  </p>
                </div>
              </div>
              {!isMobile && <ProfileDropdown logout={logout} />}
            </div>
          </div>
        </div>

        <div className="no-print">
          <div
            className="container-fluid p-0 mt-4"
            style={{
              paddingLeft: isCollapsed ? '32px' : '40px',
              paddingRight: '32px',
              paddingTop: '32px',
              paddingBottom: '32px',
            }}
          >
            <div className="d-flex justify-content-between align-items-center mb-4 gap-3">
              <button
                onClick={() => setShowFilterCard(!showFilterCard)}
                style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '50%',
                  backgroundColor: showFilterCard ? '#223843' : '#ffffff',
                  border: '1px solid #D1D3D4',
                  color: showFilterCard ? '#ffffff' : '#223843',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = showFilterCard ? '#223843' : '#223843'
                  e.currentTarget.style.color = '#ffffff'
                  e.currentTarget.style.borderColor = showFilterCard ? '#223843' : '#223843'
                  e.currentTarget.style.transform = 'scale(1.05)'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = showFilterCard ? '#223843' : '#ffffff'
                  e.currentTarget.style.color = showFilterCard ? '#ffffff' : '#223843'
                  e.currentTarget.style.borderColor = '#D1D3D4'
                  e.currentTarget.style.transform = 'scale(1)'
                }}
                title={showFilterCard ? 'Hide Filters' : 'Show Filters'}
              >
                <BsFunnelFill size={20} />
              </button>
              <Button
                onClick={() => navigate('/GenerateTC')}
                style={{
                  borderRadius: '10px',
                  padding: '8px 14px',
                  height: '44px',
                  border: 'none',
                  backgroundColor: '#223843',
                  color: '#ffffff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  whiteSpace: 'nowrap',
                }}
              >
                Generate New TC
              </Button>
            </div>

            <div
              className="tc-filter-dropdown-fix"
              style={{
                maxHeight: showFilterCard ? '1000px' : '0',
                overflow: showFilterCard ? 'visible' : 'hidden',
                transition: 'max-height 0.3s ease-in-out',
                marginBottom: showFilterCard ? '24px' : '0',
                zIndex: showFilterCard ? 10 : 1,
              }}
            >
              {showFilterCard && (
                <Card
                  style={{
                    border: '1px solid #D1D3D4',
                    borderRadius: '12px',
                    overflow: 'visible',
                    backgroundColor: '#ffffff',
                    position: 'relative',
                  }}
                >
                  <div
                    style={{
                      background: '#ffffff',
                      padding: '18px 18px 0px 18px',
                      borderBottom: '1px solid #F3F4F6',
                    }}
                  >
                    <div className="d-flex justify-content-between align-items-center">
                      <div className="d-flex align-items-center gap-3">
                        <div
                          style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '50%',
                            backgroundColor: '#223843',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <BsSearch style={{ color: '#ffffff', fontSize: '18px' }} />
                        </div>
                        <div>
                          <h5 className="m-0 fw-semibold" style={{ fontSize: '16px', color: '#111827' }}>
                            Filter Transfer Certificates
                          </h5>
                          <p className="m-0 mt-1 text-start" style={{ fontSize: '13px', color: '#6b7280' }}>
                            Select course and student.
                          </p>
                        </div>
                      </div>

                      <button
                        onClick={handleRefresh}
                        style={{
                          width: '44px',
                          height: '44px',
                          borderRadius: '50%',
                          backgroundColor: '#223843',
                          border: 'none',
                          color: '#ffffff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                        }}
                        title="Refresh"
                      >
                        <BsArrowClockwise size={18} />
                      </button>
                    </div>
                  </div>

                  <Card.Body className="p-4">
                    <Row className="g-4 text-start">
                      <Col xs={12}>
                        <div
                          style={{
                            padding: '20px',
                            backgroundColor: '#F3F4F6',
                            borderRadius: '8px',
                            border: '1px solid #D1D3D4',
                          }}
                        >
                          <div className="d-flex align-items-center gap-2 mb-3">
                            <div
                              style={{
                                width: '32px',
                                height: '32px',
                                borderRadius: '50%',
                                backgroundColor: '#223843',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              <BsSearch style={{ color: '#ffffff', fontSize: '16px' }} />
                            </div>
                            <label
                              className="fw-semibold mb-0"
                              style={{
                                color: '#111827',
                                fontSize: '14px',
                                fontFamily: 'Inter, system-ui, sans-serif',
                              }}
                            >
                              Filter by Course, Class & Student
                            </label>
                          </div>

                          <SelectableField
                            onSelect={handleSelect}
                            showCourse={true}
                            showClass={true}
                            showStudent={true}
                            abc={'abc'}
                            setData={setStudentsForSelection}
                            resetTrigger={resetKey}
                          />
                        </div>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              )}
            </div>

            <div style={{ height: showFilterCard ? '22px' : '0px' }} />

            <Card
              className="tc-table-card"
              style={{
                border: '1px solid #D1D3D4',
                borderRadius: '12px',
                overflow: 'visible',
                backgroundColor: '#ffffff',
                position: 'relative',
              }}
            >
              <div
                className="tc-table-wrapper"
                style={{
                  overflowX: 'auto',
                  overflowY: 'visible',
                  position: 'relative',
                  borderRadius: '12px',
                }}
              >
                <Table
                  className="table mb-0"
                  style={{
                    borderRadius: '12px',
                    overflow: 'visible',
                    position: 'relative',
                    marginBottom: 0,
                  }}
                >
                  <Thead>
                    <Tr style={{ backgroundColor: '#F3F4F6' }}>
                      <Th
                        style={{
                          fontSize: '13px',
                          fontWeight: '600',
                          padding: '16px',
                          border: 'none',
                          borderBottom: '1px solid #D1D3D4',
                          textAlign: 'left',
                          color: '#111827',
                          fontFamily: 'Inter, system-ui, sans-serif',
                        }}
                      >
                        TC Number
                      </Th>
                      <Th
                        style={{
                          fontSize: '13px',
                          fontWeight: '600',
                          padding: '16px',
                          border: 'none',
                          borderBottom: '1px solid #D1D3D4',
                          textAlign: 'left',
                          color: '#111827',
                          fontFamily: 'Inter, system-ui, sans-serif',
                        }}
                      >
                        Issue Date
                      </Th>
                      <Th
                        style={{
                          fontSize: '13px',
                          fontWeight: '600',
                          padding: '16px',
                          border: 'none',
                          borderBottom: '1px solid #D1D3D4',
                          textAlign: 'left',
                          color: '#111827',
                          fontFamily: 'Inter, system-ui, sans-serif',
                        }}
                      >
                        Student
                      </Th>
                      <Th
                        style={{
                          fontSize: '13px',
                          fontWeight: '600',
                          padding: '16px',
                          border: 'none',
                          borderBottom: '1px solid #D1D3D4',
                          textAlign: 'left',
                          color: '#111827',
                          fontFamily: 'Inter, system-ui, sans-serif',
                        }}
                      >
                        Course
                      </Th>
                      <Th
                        style={{
                          fontSize: '13px',
                          fontWeight: '600',
                          padding: '16px',
                          border: 'none',
                          borderBottom: '1px solid #D1D3D4',
                          textAlign: 'left',
                          color: '#111827',
                          fontFamily: 'Inter, system-ui, sans-serif',
                        }}
                      >
                        Institute
                      </Th>
                      <Th
                        style={{
                          fontSize: '13px',
                          fontWeight: '600',
                          padding: '16px',
                          border: 'none',
                          borderBottom: '1px solid #D1D3D4',
                          textAlign: 'center',
                          color: '#111827',
                          fontFamily: 'Inter, system-ui, sans-serif',
                        }}
                      >
                        Action
                      </Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {loading ? (
                      <Tr>
                        <Td
                          colSpan={6}
                          style={{
                            padding: '40px',
                            border: 'none',
                            textAlign: 'center',
                            color: '#6b7280',
                            fontSize: '14px',
                            fontFamily: 'Inter, system-ui, sans-serif',
                          }}
                        >
                          <div className="d-flex align-items-center justify-content-center gap-2">
                            <Spinner animation="border" size="sm" />
                            Loading transfer certificates...
                          </div>
                        </Td>
                      </Tr>
                    ) : transferCertificates.length > 0 ? (
                      transferCertificates.map((tc, index) => (
                        <Tr
                          key={tc?.id || `${tc?.student_id}-${tc?.course_id}-${tc?.tc_issue_date}`}
                          style={{
                            backgroundColor:
                              index % 2 === 0 ? '#ffffff' : 'rgba(243, 244, 246, 0.4)',
                            transition: 'background-color 0.2s ease',
                            borderBottom: '1px solid #E5E7EB',
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = 'rgba(37, 99, 235, 0.06)'
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor =
                              index % 2 === 0 ? '#ffffff' : 'rgba(243, 244, 246, 0.4)'
                          }}
                        >
                          <Td
                            style={{
                              fontSize: '14px',
                              padding: '16px',
                              border: 'none',
                              color: '#111827',
                              textAlign: 'left',
                              fontFamily: 'Inter, system-ui, sans-serif',
                            }}
                          >
                            {tc?.tc_number || '-'}
                          </Td>
                          <Td
                            style={{
                              fontSize: '14px',
                              padding: '16px',
                              border: 'none',
                              color: '#111827',
                              textAlign: 'left',
                              fontFamily: 'Inter, system-ui, sans-serif',
                            }}
                          >
                            {tc?.tc_issue_date
                              ? new Date(tc.tc_issue_date).toLocaleDateString('en-GB', {
                                  day: '2-digit',
                                  month: 'long',
                                  year: 'numeric',
                                })
                              : '-'}
                          </Td>
                          <Td
                            style={{
                              fontSize: '14px',
                              padding: '16px',
                              border: 'none',
                              color: '#111827',
                              textAlign: 'left',
                              fontFamily: 'Inter, system-ui, sans-serif',
                            }}
                          >
                            {tc?.student?.name || '-'}
                          </Td>
                          <Td
                            style={{
                              fontSize: '14px',
                              padding: '16px',
                              border: 'none',
                              color: '#111827',
                              textAlign: 'left',
                              fontFamily: 'Inter, system-ui, sans-serif',
                            }}
                          >
                            {tc?.course?.course_name || tc?.course?.name || '-'}
                          </Td>
                          <Td
                            style={{
                              fontSize: '14px',
                              padding: '16px',
                              border: 'none',
                              color: '#111827',
                              textAlign: 'left',
                              fontFamily: 'Inter, system-ui, sans-serif',
                            }}
                          >
                            {tc?.institute?.institute_name || tc?.institute?.name || '-'}
                          </Td>
                          <Td
                            className="text-center"
                            style={{ padding: '16px', border: 'none', verticalAlign: 'middle' }}
                          >
                            {tc?.id != null ? (
                              <div
                                className="d-inline-flex align-items-center justify-content-center gap-2"
                              >
                                <button
                                  type="button"
                                  onClick={() => navigate(`/TransferCertificate/${tc.id}`)}
                                  style={{
                                    width: '36px',
                                    height: '36px',
                                    borderRadius: '50%',
                                    backgroundColor: '#223843',
                                    border: 'none',
                                    color: '#ffffff',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease',
                                  }}
                                  title="View Transfer Certificate"
                                >
                                  <BsEyeFill size={18} />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => openEditModal(tc)}
                                  style={{
                                    width: '36px',
                                    height: '36px',
                                    borderRadius: '50%',
                                    backgroundColor: '#ffffff',
                                    border: '1px solid #223843',
                                    color: '#223843',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease',
                                  }}
                                  title="Edit Transfer Certificate"
                                >
                                  <BsPencilFill size={16} />
                                </button>
                              </div>
                            ) : (
                              <span style={{ color: '#9ca3af', fontSize: '13px' }}>—</span>
                            )}
                          </Td>
                        </Tr>
                      ))
                    ) : (
                      <Tr>
                        <Td
                          colSpan={6}
                          className="text-center"
                          style={{
                            padding: '40px',
                            fontSize: '14px',
                            color: '#6b7280',
                            border: 'none',
                            fontFamily: 'Inter, system-ui, sans-serif',
                          }}
                        >
                          {filters.student && filters.course
                            ? 'No Transfer Certificate found for the selected filters.'
                            : 'Select course and student to view records.'}
                        </Td>
                      </Tr>
                    )}
                  </Tbody>
                </Table>
              </div>
            </Card>
          </div>
        </div>
      </div>

      <style>
        {`
          /* dialogClassName is applied ON .modal-dialog, not a parent */
          .modal-dialog.edit-tc-modal {
            max-width: min(1100px, calc(100vw - 32px)) !important;
            width: min(1100px, calc(100vw - 32px)) !important;
            margin: 1.75rem auto;
          }
          @media (max-width: 768px) {
            .modal-dialog.edit-tc-modal {
              max-width: calc(100vw - 16px) !important;
              width: calc(100vw - 16px) !important;
              margin: 8px auto;
            }
          }
        `}
      </style>

      <Modal
        show={Boolean(editingTc)}
        onHide={closeEditModal}
        centered
        scrollable
        size="xl"
        dialogClassName="edit-tc-modal"
        backdrop={updatingTc || loadingEditTc ? 'static' : true}
        keyboard={!updatingTc && !loadingEditTc}
      >
        <Modal.Header
          closeButton={!updatingTc && !loadingEditTc}
          style={{
            padding: isMobile ? '16px 18px' : '20px 28px',
            borderBottom: '1px solid #E5E7EB',
          }}
        >
          <Modal.Title
            style={{
              fontFamily: 'Inter, system-ui, sans-serif',
              fontWeight: 700,
              fontSize: isMobile ? '18px' : '22px',
              color: '#223843',
            }}
          >
            Edit Transfer Certificate
            {editingTc?.tc_number ? ` — ${editingTc.tc_number}` : ''}
          </Modal.Title>
        </Modal.Header>

        <Form onSubmit={handleEditSubmit}>
          <Modal.Body
            style={{
              fontFamily: 'Inter, system-ui, sans-serif',
              padding: isMobile ? '20px 18px' : '28px 32px',
            }}
          >
            {loadingEditTc ? (
              <div className="d-flex align-items-center justify-content-center gap-2 py-5 text-muted">
                <Spinner animation="border" size="sm" />
                Loading certificate details...
              </div>
            ) : (
              <>
            {(editingTc?.student?.name || editingTc?.course?.course_name || editingTc?.course?.name) && (
              <div
                className="mb-4 text-start"
                style={{
                  padding: '12px 14px',
                  backgroundColor: '#F3F4F6',
                  borderRadius: '8px',
                  border: '1px solid #D1D3D4',
                  fontSize: '13px',
                  color: '#4b5563',
                }}
              >
                {editingTc?.student?.name && (
                  <div>
                    <strong style={{ color: '#111827' }}>Student:</strong>{' '}
                    {editingTc.student.name}
                  </div>
                )}
                {(editingTc?.course?.course_name || editingTc?.course?.name) && (
                  <div className="mt-1">
                    <strong style={{ color: '#111827' }}>Course:</strong>{' '}
                    {editingTc.course?.course_name || editingTc.course?.name}
                  </div>
                )}
              </div>
            )}

            <Row className="g-3 text-start">
              <Col xs={12} md={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold text-muted" style={{ fontSize: 13 }}>
                    TC Issue Date
                  </Form.Label>
                  <Form.Control
                    type="datetime-local"
                    name="tc_issue_date"
                    value={editFormData.tc_issue_date}
                    onChange={handleEditInputChange}
                    disabled={updatingTc || loadingEditTc}
                    isInvalid={Boolean(editFormErrors.tc_issue_date)}
                    style={{ borderRadius: 10, height: '48px' }}
                  />
                  <Form.Control.Feedback type="invalid">
                    {editFormErrors.tc_issue_date}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>

              <Col xs={12} md={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold text-muted" style={{ fontSize: 13 }}>
                    Course Completion Date
                  </Form.Label>
                  <Form.Control
                    type="date"
                    name="course_completion_date"
                    value={editFormData.course_completion_date}
                    onChange={handleEditInputChange}
                    disabled={updatingTc || loadingEditTc}
                    isInvalid={Boolean(editFormErrors.course_completion_date)}
                    style={{ borderRadius: 10, height: '48px' }}
                  />
                  <Form.Control.Feedback type="invalid">
                    {editFormErrors.course_completion_date}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>

              <Col xs={12}>
                <Form.Group>
                  <Form.Label className="fw-semibold text-muted" style={{ fontSize: 13 }}>
                    Reason (max 255)
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="reason"
                    value={editFormData.reason}
                    onChange={handleEditInputChange}
                    maxLength={255}
                    disabled={updatingTc || loadingEditTc}
                    isInvalid={Boolean(editFormErrors.reason)}
                    placeholder="e.g. Relocated to another city"
                    style={{ borderRadius: 10, height: '48px' }}
                  />
                  <Form.Control.Feedback type="invalid">
                    {editFormErrors.reason}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>

              <Col xs={12}>
                <Form.Group>
                  <Form.Label className="fw-semibold text-muted" style={{ fontSize: 13 }}>
                    Remarks
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    name="remarks"
                    value={editFormData.remarks}
                    onChange={handleEditInputChange}
                    disabled={updatingTc || loadingEditTc}
                    isInvalid={Boolean(editFormErrors.remarks)}
                    placeholder="e.g. Updated after verification"
                    style={{ borderRadius: 10 }}
                  />
                  <Form.Control.Feedback type="invalid">
                    {editFormErrors.remarks}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>
              </>
            )}
          </Modal.Body>

          <Modal.Footer
            style={{
              padding: isMobile ? '16px 18px' : '20px 28px',
              borderTop: '1px solid #E5E7EB',
              gap: '10px',
            }}
          >
            <button
              type="button"
              onClick={closeEditModal}
              disabled={updatingTc || loadingEditTc}
              style={{
                borderRadius: '10px',
                padding: '10px 16px',
                border: '1px solid #D1D3D4',
                backgroundColor: '#ffffff',
                color: '#223843',
                fontWeight: 600,
                fontSize: '14px',
                cursor: updatingTc || loadingEditTc ? 'not-allowed' : 'pointer',
                opacity: updatingTc || loadingEditTc ? 0.6 : 1,
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={updatingTc || loadingEditTc}
              style={{
                borderRadius: '10px',
                padding: '10px 16px',
                border: 'none',
                backgroundColor: '#223843',
                color: '#ffffff',
                fontWeight: 600,
                fontSize: '14px',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                cursor: updatingTc || loadingEditTc ? 'not-allowed' : 'pointer',
                opacity: updatingTc || loadingEditTc ? 0.85 : 1,
              }}
            >
              {updatingTc && <Spinner animation="border" size="sm" />}
              {updatingTc ? 'Saving...' : 'Save Changes'}
            </button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  )
}

export default TransferCertificateList
